import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Stack,
  TextField,
} from "@mui/material";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";

import {
  getPriceHistoryRecordUpdateRequestPreview,
  validatePriceHistoryRecordFormValues,
} from "../../services/beefAiPricing";
import { searchGatewayRetailers } from "../../services/missingReports";
import GatewayRequestDebugPanel from "../../components/Analytics/Missing/GatewayRequestDebugPanel";
import RevisionDeviationDetails from "./RevisionDeviationDetails";
import PriceHistoryRecordFieldGroup from "./PriceHistoryRecordFieldGroup";
import PriceHistoryRecordFieldRow from "./PriceHistoryRecordFieldRow";
import PriceHistoryRecordSaveConfirmDialog from "./PriceHistoryRecordSaveConfirmDialog";
import PricingReportRetailerAutocomplete from "./PricingReportRetailerAutocomplete";
import {
  buildRetailerOptionsWithCurrentName,
  findRetailerOptionByName,
} from "./pricingReportProductFilter";
import { cadCurrencyFormat } from "./constants";

const DEFAULT_UNIT_CHOICES = ["kg"];

function normalizeRetailerNameKey(value) {
  return String(value || "").trim().toLowerCase();
}

async function resolveRetailerRefIdForName(retailerName) {
  const trimmed = String(retailerName || "").trim();
  if (!trimmed) {
    return "";
  }

  const nameKey = normalizeRetailerNameKey(trimmed);
  try {
    const matches = await searchGatewayRetailers(trimmed);
    const exactMatch = matches.find(
      (option) =>
        normalizeRetailerNameKey(option?.name || option?.label) === nameKey
    );
    return exactMatch?.id || "";
  } catch (_) {
    return "";
  }
}

function buildFormValuesFromFormState(formState, resolveProvinceForApi, formatProductOptionLabel) {
  const provinceCode = formState.selectedProvince?.value;
  const provinceForApi = resolveProvinceForApi?.(provinceCode) ?? provinceCode;

  return {
    recordAt: formState.recordAt?.isValid?.()
      ? formState.recordAt.startOf("day").toISOString()
      : "",
    productId: formState.selectedProduct?.id || "",
    productName:
      formatProductOptionLabel?.(formState.selectedProduct) ||
      formState.selectedProduct?.name ||
      "",
    retailer: formState.retailer?.trim() || "",
    retailerRefId: formState.retailerRefId?.trim() || "",
    province: provinceForApi || "",
    price: formState.price === "" ? NaN : Number(formState.price),
    unitPrice: formState.unitPrice === "" ? NaN : Number(formState.unitPrice),
    unit: formState.unit?.trim() || "",
    unitQuantity:
      formState.unitQuantity === "" || formState.unitQuantity == null
        ? null
        : Number(formState.unitQuantity),
  };
}

function buildSaveSummaryLines(formState, formatProductOptionLabel) {
  const productLabel =
    formatProductOptionLabel?.(formState.selectedProduct) ||
    formState.selectedProduct?.name ||
    "—";

  const formatCurrency = (value) => {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? cadCurrencyFormat.format(parsed) : "—";
  };

  return [
    {
      label: "Date",
      value: formState.recordAt?.isValid?.() ? formState.recordAt.format("YYYY-MM-DD") : "—",
    },
    { label: "Product", value: productLabel },
    { label: "Retailer", value: formState.retailer || "—" },
    { label: "Province", value: formState.selectedProvince?.label || "—" },
    { label: "Price", value: formatCurrency(formState.price) },
    { label: "Unit price", value: formatCurrency(formState.unitPrice) },
    {
      label: "Unit quantity",
      value: formState.unitQuantity === "" ? "—" : String(formState.unitQuantity),
    },
    { label: "Unit", value: formState.unit || "—" },
  ];
}

function buildFormStateFromItem(item, productFilterChoices, provinceChoices) {
  if (!item) {
    return {
      recordAt: null,
      selectedProduct: null,
      retailer: "",
      retailerRefId: "",
      selectedProvince: null,
      price: "",
      unitPrice: "",
      unit: "kg",
      unitQuantity: "",
    };
  }

  const selectedProduct =
    productFilterChoices.find((product) => product.id === item.productId) ||
    (item.productId
      ? {
          id: item.productId,
          name: item.product !== "—" ? item.product : item.productId,
        }
      : null);

  const selectedProvince =
    provinceChoices.find((option) => option.value === item.provinceCode) ||
    (item.provinceCode
      ? { value: item.provinceCode, label: item.province !== "—" ? item.province : item.provinceCode }
      : null);

  return {
    recordAt: item.recordAtValue?.isValid?.() ? item.recordAtValue : null,
    selectedProduct,
    retailer: item.retailerName || "",
    retailerRefId: item.retailerRefId || "",
    selectedProvince,
    price: item.priceValue != null ? String(item.priceValue) : "",
    unitPrice: item.unitPriceValue != null ? String(item.unitPriceValue) : "",
    unit: item.unit || "kg",
    unitQuantity: item.unitQuantityValue != null ? String(item.unitQuantityValue) : "",
  };
}

function PriceHistoryRecordEditForm({
  item,
  productFilterChoices,
  retailerChoices,
  provinceChoices,
  unitChoices = DEFAULT_UNIT_CHOICES,
  formatProductOptionLabel,
  isPublishedProductsLoading,
  isLoadingFilterOptions,
  resolveProvinceForApi,
  onSaveRecord,
  onCancel,
  onSavingChange,
}) {
  const initialFormState = useMemo(
    () => buildFormStateFromItem(item, productFilterChoices, provinceChoices),
    [item, productFilterChoices, provinceChoices]
  );

  const [formState, setFormState] = useState(initialFormState);
  const [isSaving, setIsSaving] = useState(false);
  const [isConfirmDialogOpen, setIsConfirmDialogOpen] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [successMessage, setSuccessMessage] = useState("");

  useEffect(() => {
    setFormState(initialFormState);
    setErrorMessage("");
    setSuccessMessage("");
  }, [initialFormState, item?.id]);

  useEffect(() => {
    onSavingChange?.(isSaving);
  }, [isSaving, onSavingChange]);

  const retailerOptions = useMemo(
    () => buildRetailerOptionsWithCurrentName(retailerChoices, item?.retailerName),
    [retailerChoices, item?.retailerName]
  );

  const selectedRetailerOption = useMemo(
    () => findRetailerOptionByName(retailerOptions, formState.retailer),
    [formState.retailer, retailerOptions]
  );

  const unitOptions = useMemo(() => {
    const options = [...unitChoices];
    if (item?.unit && !options.includes(item.unit)) {
      options.push(item.unit);
    }
    return options;
  }, [unitChoices, item?.unit]);

  const formValuesForApi = useMemo(
    () => buildFormValuesFromFormState(formState, resolveProvinceForApi, formatProductOptionLabel),
    [formState, resolveProvinceForApi, formatProductOptionLabel]
  );

  const saveSummaryLines = useMemo(
    () => buildSaveSummaryLines(formState, formatProductOptionLabel),
    [formState, formatProductOptionLabel]
  );

  const saveRequestPreview = useMemo(
    () => getPriceHistoryRecordUpdateRequestPreview(item?.id, formValuesForApi),
    [item?.id, formValuesForApi]
  );

  const handleRetailerChange = useCallback((nextRetailer) => {
    const retailerName = nextRetailer?.name || "";
    setFormState((prev) => ({
      ...prev,
      retailer: retailerName,
      retailerRefId: "",
    }));

    if (!retailerName) {
      return;
    }

    resolveRetailerRefIdForName(retailerName).then((retailerRefId) => {
      setFormState((prev) =>
        prev.retailer === retailerName ? { ...prev, retailerRefId } : prev
      );
    });
  }, []);

  const handleCancel = useCallback(() => {
    setFormState(initialFormState);
    setErrorMessage("");
    setSuccessMessage("");
    onCancel?.();
  }, [initialFormState, onCancel]);

  const handleSaveClick = useCallback(() => {
    if (!item?.id) return;

    setErrorMessage("");
    setSuccessMessage("");

    try {
      validatePriceHistoryRecordFormValues(formValuesForApi);
      setIsConfirmDialogOpen(true);
    } catch (error) {
      setErrorMessage(error?.message || "Fix validation errors before saving.");
    }
  }, [item?.id, formValuesForApi]);

  const handleConfirmSave = useCallback(async () => {
    if (!item?.id) return;

    setIsSaving(true);
    try {
      await onSaveRecord(item.id, formValuesForApi);
      setIsConfirmDialogOpen(false);
      setSuccessMessage("Record saved.");
    } catch (error) {
      setIsConfirmDialogOpen(false);
      setErrorMessage(error?.message || "Failed to save price history record.");
    } finally {
      setIsSaving(false);
    }
  }, [item?.id, formValuesForApi, onSaveRecord]);

  const handleCloseConfirmDialog = useCallback(() => {
    if (isSaving) return;
    setIsConfirmDialogOpen(false);
  }, [isSaving]);

  if (!item) {
    return null;
  }

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Stack spacing={2.5}>
        {errorMessage ? <Alert severity="error">{errorMessage}</Alert> : null}
        {successMessage ? <Alert severity="success">{successMessage}</Alert> : null}

        <PriceHistoryRecordFieldGroup title="Record">
          <PriceHistoryRecordFieldRow>
            <DatePicker
              label="Date"
              value={formState.recordAt}
              onChange={(nextDate) => setFormState((prev) => ({ ...prev, recordAt: nextDate }))}
              slotProps={{ textField: { fullWidth: true, size: "small" } }}
            />
          </PriceHistoryRecordFieldRow>

          <PriceHistoryRecordFieldRow columnWidth={12}>
            <Autocomplete
              fullWidth
              size="small"
              options={productFilterChoices}
              value={formState.selectedProduct}
              onChange={(_, nextProduct) =>
                setFormState((prev) => ({ ...prev, selectedProduct: nextProduct }))
              }
              getOptionLabel={(product) => formatProductOptionLabel(product)}
              isOptionEqualToValue={(left, right) => left?.id === right?.id}
              renderOption={(props, option) => {
                const { key: _ignoredMuiKey, ...listItemProps } = props;
                return (
                  <Box component="li" key={option.id} {...listItemProps}>
                    {formatProductOptionLabel(option)}
                  </Box>
                );
              }}
              renderInput={(params) => <TextField {...params} label="Product" />}
              loading={isPublishedProductsLoading}
              disabled={isSaving}
            />
          </PriceHistoryRecordFieldRow>

          <PriceHistoryRecordFieldRow>
            <PricingReportRetailerAutocomplete
              options={retailerOptions}
              value={selectedRetailerOption}
              onChange={handleRetailerChange}
              showCounts={false}
              loading={isLoadingFilterOptions}
              disabled={isSaving}
            />

            <Autocomplete
              fullWidth
              size="small"
              options={provinceChoices}
              value={formState.selectedProvince}
              onChange={(_, nextProvince) =>
                setFormState((prev) => ({ ...prev, selectedProvince: nextProvince }))
              }
              getOptionLabel={(option) => option?.label ?? ""}
              isOptionEqualToValue={(left, right) => left?.value === right?.value}
              renderInput={(params) => <TextField {...params} label="Province" />}
              loading={isLoadingFilterOptions}
              disabled={isSaving}
            />
          </PriceHistoryRecordFieldRow>
        </PriceHistoryRecordFieldGroup>

        <PriceHistoryRecordFieldGroup title="Pricing">
          <PriceHistoryRecordFieldRow>
            <TextField
              fullWidth
              size="small"
              label="Price"
              type="number"
              inputProps={{ min: 0, step: "0.01" }}
              value={formState.price}
              onChange={(event) => setFormState((prev) => ({ ...prev, price: event.target.value }))}
              disabled={isSaving}
            />

            <TextField
              fullWidth
              size="small"
              label="Unit price"
              type="number"
              inputProps={{ min: 0, step: "0.01" }}
              value={formState.unitPrice}
              onChange={(event) =>
                setFormState((prev) => ({ ...prev, unitPrice: event.target.value }))
              }
              disabled={isSaving}
            />
          </PriceHistoryRecordFieldRow>

          <PriceHistoryRecordFieldRow>
            <TextField
              fullWidth
              size="small"
              label="Unit quantity"
              type="number"
              inputProps={{ min: 0, step: "0.01" }}
              value={formState.unitQuantity}
              onChange={(event) =>
                setFormState((prev) => ({ ...prev, unitQuantity: event.target.value }))
              }
              disabled={isSaving}
            />

            <Autocomplete
              fullWidth
              size="small"
              options={unitOptions}
              value={formState.unit || null}
              onChange={(_, nextUnit) => setFormState((prev) => ({ ...prev, unit: nextUnit || "" }))}
              renderInput={(params) => <TextField {...params} label="Unit" />}
              disabled={isSaving}
            />
          </PriceHistoryRecordFieldRow>
        </PriceHistoryRecordFieldGroup>

        <RevisionDeviationDetails
          revisionStatus={item.revisionStatus}
          revisionMetadata={item.revisionMetadata}
          unitPriceValue={item.unitPriceValue}
        />

        <GatewayRequestDebugPanel
          title="Beef AI debug request preview (Price History)"
          request={saveRequestPreview}
        />

        <Stack direction="row" spacing={1} justifyContent="flex-start">
          <Button
            variant="outlined"
            color="secondary"
            size="small"
            onClick={handleCancel}
            disabled={isSaving}
          >
            Cancel
          </Button>
          <Button
            variant="outlined"
            color="secondary"
            size="small"
            onClick={handleSaveClick}
            disabled={isSaving}
          >
            {isSaving ? "Saving…" : "Save"}
          </Button>
        </Stack>

        <PriceHistoryRecordSaveConfirmDialog
          open={isConfirmDialogOpen}
          summaryLines={saveSummaryLines}
          isSaving={isSaving}
          onCancel={handleCloseConfirmDialog}
          onConfirm={handleConfirmSave}
        />
      </Stack>
    </LocalizationProvider>
  );
}

export default PriceHistoryRecordEditForm;
